import { useCallback, useMemo, useRef, useState } from 'react';
import { focusSVGElement, matchesX } from '../utils';
function getSeriesDetail(internalSeries, targetX) {
    var series = internalSeries.series, color = internalSeries.color;
    if (series.type === 'threshold') {
        return {
            key: series.title,
            value: series.valueFormatter ? series.valueFormatter(series.y) : series.y,
            color: color,
            markerType: 'dashed'
        };
    }
    var match = series.data.filter(function (_a) {
        var x = _a.x;
        return matchesX(targetX, x);
    });
    if (match.length) {
        return {
            key: series.title,
            value: series.valueFormatter ? series.valueFormatter(match[0].y, targetX) : match[0].y,
            color: color,
            markerType: series.type === 'line' ? 'line' : 'rectangle'
        };
    }
    return null;
}
export function usePopover(_a) {
    var _b;
    var visibleSeries = _a.visibleSeries, xTickFormatter = _a.xTickFormatter;
    var _c = useState(false), isPopoverOpen = _c[0], setPopoverOpen = _c[1];
    var _d = useState(false), isPopoverPinned = _d[0], setPopoverPinned = _d[1];
    var _e = useState(null), popoverData = _e[0], setPopoverData = _e[1];
    var popoverDismissedRecently = useRef(false);
    var showPopover = useCallback(function (popoverData) {
        if (isPopoverPinned || popoverDismissedRecently.current) {
            return;
        }
        setPopoverData(popoverData);
        setPopoverOpen(true);
    }, [isPopoverPinned]);
    var pinPopover = useCallback(function (pinned) {
        if (pinned === void 0) { pinned = true; }
        setPopoverPinned(pinned);
    }, []);
    var hidePopover = useCallback(function () {
        setPopoverOpen(false);
        setPopoverPinned(false);
    }, []);
    var onPopoverDismiss = function (outsideClick) {
        setPopoverOpen(false);
        setPopoverPinned(false);
        // Return focus back to the segment if possible
        var target = (popoverData === null || popoverData === void 0 ? void 0 : popoverData.focusTarget) || (popoverData === null || popoverData === void 0 ? void 0 : popoverData.trigger);
        if (target && !outsideClick) {
            // We disable `returnFocus` on the popover because the library won't be able to focus an SVG element in IE11.
            // Therefore we need to return focus ourselves.
            // We are using `setTimeout` as suggested by react-focus-lock when using `returnFocus={false}`.
            // See https://github.com/theKashey/react-focus-lock#unmounting-and-focus-management
            setTimeout(function () {
                popoverDismissedRecently.current = true;
                focusSVGElement(target);
                popoverDismissedRecently.current = false;
            }, 0);
        }
    };
    // Default popover title to the name of the series
    var popoverTitle = (_b = popoverData === null || popoverData === void 0 ? void 0 : popoverData.series) === null || _b === void 0 ? void 0 : _b.series.title;
    if ((popoverData === null || popoverData === void 0 ? void 0 : popoverData.x) !== null && (popoverData === null || popoverData === void 0 ? void 0 : popoverData.x) !== undefined) {
        // But more accurately, use the title of the current x value, if available
        popoverTitle = xTickFormatter ? xTickFormatter(popoverData.x) : popoverData.x.toString();
    }
    var popoverProps = isPopoverOpen && popoverData
        ? {
            title: popoverTitle,
            trigger: popoverData.trigger,
            dismissButton: isPopoverPinned,
            onDismiss: onPopoverDismiss
        }
        : null;
    var popoverDetails = useMemo(function () {
        if (!popoverData) {
            return [];
        }
        if (popoverData.series) {
            // Details for a single series
            var detail = getSeriesDetail(popoverData.series, popoverData.x);
            return detail ? [detail] : [];
        }
        var details = [];
        // Details for a group
        visibleSeries.forEach(function (series) {
            var detail = getSeriesDetail(series, popoverData.x);
            if (detail) {
                details.push(detail);
            }
        });
        return details;
    }, [popoverData, visibleSeries]);
    return {
        isPopoverOpen: isPopoverOpen,
        isPopoverPinned: isPopoverPinned,
        showPopover: showPopover,
        hidePopover: hidePopover,
        pinPopover: pinPopover,
        popoverProps: popoverProps,
        popoverDetails: popoverDetails,
        popoverData: popoverData
    };
}
//# sourceMappingURL=use-popover.js.map